// Encrypted-at-rest storage for the PCRS username + password.
//
// The credentials are encrypted with AES-GCM via the Web Crypto API and only
// the ciphertext is written to localStorage. The AES key is generated as a
// NON-EXTRACTABLE CryptoKey and kept in IndexedDB — script can use it to
// encrypt/decrypt but cannot read its raw bytes out. Both the key and the
// ciphertext persist across browser restarts.
//
// Caveat: this protects against the credentials sitting in plaintext, but it
// is not protection against an attacker with access to this browser profile —
// the key lives in the same profile. It is obfuscation, not a secret vault.

const DATABASE_NAME = "kinesin-pcrs";
const DATABASE_VERSION = 1;
const KEY_STORE_NAME = "keys";
const KEY_ID = "pcrs-credential-key";
const CREDENTIALS_STORAGE_KEY = "kinesin-pcrs-credentials";
const IV_BYTE_LENGTH = 12;

const openDatabase = () =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(KEY_STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const readKeyFromDatabase = async () => {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(KEY_STORE_NAME, "readonly");
    const request = transaction.objectStore(KEY_STORE_NAME).get(KEY_ID);
    request.onsuccess = () => resolve(request.result ?? null);
    request.onerror = () => reject(request.error);
  });
};

const writeKeyToDatabase = async (key) => {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(KEY_STORE_NAME, "readwrite");
    transaction.objectStore(KEY_STORE_NAME).put(key, KEY_ID);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

// Reuse the stored key if present, otherwise mint a fresh non-extractable one.
const getOrCreateKey = async () => {
  const existingKey = await readKeyFromDatabase();
  if (existingKey) return existingKey;

  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
  await writeKeyToDatabase(key);

  return key;
};

const toBase64 = (bytes) => btoa(String.fromCharCode(...bytes));

const fromBase64 = (text) =>
  Uint8Array.from(atob(text), (character) => character.charCodeAt(0));

export const saveStoredCredentials = async ({ username, password }) => {
  const key = await getOrCreateKey();
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTE_LENGTH));
  const plaintext = new TextEncoder().encode(
    JSON.stringify({ username, password })
  );
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    plaintext
  );

  localStorage.setItem(
    CREDENTIALS_STORAGE_KEY,
    JSON.stringify({
      iv: toBase64(iv),
      data: toBase64(new Uint8Array(ciphertext)),
    })
  );
};

export const readStoredCredentials = async () => {
  const stored = localStorage.getItem(CREDENTIALS_STORAGE_KEY);
  if (!stored) return null;

  let parsed;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }

  if (!parsed?.iv || !parsed?.data) return null;

  try {
    const key = await getOrCreateKey();
    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fromBase64(parsed.iv) },
      key,
      fromBase64(parsed.data)
    );

    return JSON.parse(new TextDecoder().decode(plaintext));
  } catch {
    // Key rotated or ciphertext corrupted — treat as "no stored credentials".
    return null;
  }
};

export const clearStoredCredentials = () => {
  localStorage.removeItem(CREDENTIALS_STORAGE_KEY);
};
